/**
 * Hook: useClientJourney
 *
 * Busca e agrega dados do cliente de múltiplas fontes:
 * - Cliente base (dados pessoais)
 * - Agendamentos (leads/contatos)
 * - Contratos (vendas)
 *
 * Retorna uma timeline unificada de todos os eventos
 */

import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/features/auth';
import { formatStatusLabel } from '@/lib/statusLabels';
import { parseAgendamentoTimestamp } from '@/lib/utils';

export interface TimelineEvent {
  id: string;
  type: 'AGENDAMENTO' | 'CONVERSAO' | 'CONTRATO' | 'PROPOSTA' | 'STATUS_CHANGE';
  title: string;
  description: string;
  timestamp: Date;
  /** Nome de quem registrou/gerou a ação */
  actorName?: string;
  data: any;
  metadata?: {
    icon?: string;
    color?: string;
    actionable?: boolean;
  };
}

export interface ClientJourney {
  cliente: {
    id: string;
    nome: string;
    cpf: string;
    email: string;
    telefone: string;
    status: string;
    created_at: string;
  };
  stats: {
    total_agendamentos: number;
    total_contratos: number;
    primeira_interacao?: Date;
    ultima_interacao?: Date;
  };
  events: TimelineEvent[];
}

/**
 * Hook para buscar jornada completa do cliente
 *
 * @param clienteId - ID do cliente
 * @param cardId - ID do card do pipeline (opcional) — quando informado, inclui as
 *                 movimentações de stage (card_history) desse card na timeline
 * @returns Jornada do cliente com timeline de eventos
 *
 * @example
 * ```tsx
 * const { journey, loading, error } = useClientJourney(clienteId);
 *
 * if (loading) return <Loading />;
 * if (error) return <Error message={error.message} />;
 *
 * return <Timeline events={journey.events} />;
 * ```
 */
export function useClientJourney(clienteId: string, cardId?: string) {
  const { profile } = useAuth();

  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ['client-journey', clienteId, cardId, profile?.tenant_id],
    queryFn: async () => {
      if (!profile?.tenant_id) {
        throw new Error('Tenant ID não encontrado');
      }

      return await fetchClientJourney(clienteId, profile.tenant_id, cardId);
    },
    enabled: !!clienteId && !!profile?.tenant_id,
    staleTime: 1000 * 60 * 5, // 5 minutos
    retry: 2
  });

  return {
    journey: data,
    loading: isLoading,
    error: error as Error | null,
    refetch
  };
}

const CARD_ACTION_LABELS: Record<string, string> = {
  criado: 'Lead criado no pipeline',
  movido_para_stage: 'Movido de coluna',
  editado: 'Lead editado',
  deletado: 'Lead removido',
  transferido: 'Lead transferido'
};

/**
 * Buscar dados do cliente e montar a jornada
 */
async function fetchClientJourney(
  clienteId: string,
  tenantId: number,
  cardId?: string
): Promise<ClientJourney> {
  // 1. Buscar dados do cliente
  const { data: cliente, error: clienteError } = await supabase
    .from('clientes')
    .select('id, nome, cpf, email, tel1, status, created_at')
    .eq('id', clienteId)
    .eq('tenant_id', tenantId)
    .single();

  if (clienteError || !cliente) {
    throw new Error(`Cliente ${clienteId} não encontrado`);
  }

  // 2. Buscar agendamentos do cliente
  const { data: agendamentos = [] } = await supabase
    .from('agendamentos')
    .select(`
      id,
      data_agendamento,
      hora_agendamento,
      status,
      observacoes,
      cliente_id,
      created_by,
      created_at,
      relacionador:consultores!agendamentos_relacionador_id_fkey(id, nome)
    `)
    .eq('cliente_id', clienteId)
    .eq('tenant_id', tenantId)
    .order('data_agendamento', { ascending: false });

  // 3. Buscar contratos do cliente
  const { data: contratos = [] } = await supabase
    .from('contratos')
    .select('id, contrato_numero, status, created_at, created_by, valor_parcela, cliente_id, bem_id, cota, data_assembleia, parcelas_pagas, consultor_id, grupo_id')
    .eq('cliente_id', clienteId)
    .eq('tenant_id', tenantId)
    .order('created_at', { ascending: false });

  // 3b. Buscar dados do bem (asset) vinculado aos contratos
  let bemsMap: Record<string, any> = {};
  const bemIds = [...new Set(contratos.map(c => c.bem_id).filter(Boolean))];
  if (bemIds.length > 0) {
    const { data: bemsData } = await supabase
      .from('bens')
      .select('*')
      .in('id', bemIds);

    if (bemsData) {
      bemsMap = Object.fromEntries(bemsData.map(b => [b.id, b]));
    }
  }

  // 3c. Buscar dados dos consultores relacionados aos contratos
  let consultoresMap: Record<string, any> = {};
  const consultorIds = [...new Set(contratos.map(c => c.consultor_id).filter(Boolean))];
  if (consultorIds.length > 0) {
    const { data: consultoresData } = await supabase
      .from('consultores')
      .select('id, nome')
      .in('id', consultorIds);

    if (consultoresData) {
      consultoresMap = Object.fromEntries(consultoresData.map(c => [c.id, c]));
    }
  }

  // 3d. Buscar dados dos grupos relacionados aos contratos
  let gruposMap: Record<string, any> = {};
  const grupoIds = [...new Set(contratos.map(c => c.grupo_id).filter(Boolean))];
  if (grupoIds.length > 0) {
    const { data: gruposData } = await supabase
      .from('grupos')
      .select('id, numero_grupo, especie, titulo')
      .in('id', grupoIds);

    if (gruposData) {
      gruposMap = Object.fromEntries(gruposData.map(g => [g.id, g]));
    }
  }

  // Enriquecer contratos com dados relacionados
  const contratosEnriquecidos = contratos.map(c => ({
    ...c,
    bens: bemsMap[c.bem_id] || null,
    cliente: cliente,
    clientes: cliente,
    consultor: consultoresMap[c.consultor_id] ? { nome: consultoresMap[c.consultor_id].nome } : null,
    grupo: gruposMap[c.grupo_id] || null
  }));

  // 3e. Buscar histórico de movimentações do card (mudanças de stage)
  let cardHistory: Array<{
    id: string;
    usuario_id: string;
    acao: string;
    stage_anterior_id: string | null;
    stage_novo_id: string | null;
    motivo: string | null;
    created_at: string | null;
  }> = [];
  if (cardId) {
    const { data: historyData } = await supabase
      .from('card_history')
      .select('id, usuario_id, acao, stage_anterior_id, stage_novo_id, motivo, created_at')
      .eq('card_id', cardId)
      .order('created_at', { ascending: false });

    cardHistory = historyData || [];
  }

  // 3f. Buscar nomes dos stages envolvidos nas movimentações
  let stagesMap: Record<string, string> = {};
  const stageIds = [
    ...new Set(
      cardHistory
        .flatMap((h) => [h.stage_anterior_id, h.stage_novo_id])
        .filter(Boolean)
    ),
  ] as string[];
  if (stageIds.length > 0) {
    const { data: stagesData } = await supabase
      .from('pipeline_stages')
      .select('id, nome')
      .in('id', stageIds);

    if (stagesData) {
      stagesMap = Object.fromEntries(stagesData.map((s) => [s.id, s.nome]));
    }
  }

  // Perfis de quem registrou agendamentos/contratos/movimentações
  const createdByUuids = [
    ...new Set([
      ...agendamentos.map((a: { created_by?: string }) => a.created_by),
      ...contratos.map((c) => c.created_by),
      ...cardHistory.map((h) => h.usuario_id),
    ].filter(Boolean)),
  ] as string[];

  let profilesMap: Record<string, string> = {};
  if (createdByUuids.length > 0) {
    const { data: profilesData } = await supabase
      .from('profiles')
      .select('id, first_name, last_name')
      .in('id', createdByUuids);

    if (profilesData) {
      profilesMap = Object.fromEntries(
        profilesData.map((p) => [
          p.id,
          [p.first_name, p.last_name].filter(Boolean).join(' ') || 'Usuário',
        ])
      );
    }
  }

  const resolveActor = (
    type: TimelineEvent['type'],
    data: Record<string, unknown>
  ): string | undefined => {
    if (type === 'AGENDAMENTO') {
      const relacionador = data.relacionador as { nome?: string } | null;
      const createdBy = data.created_by as string | undefined;
      return relacionador?.nome || (createdBy ? profilesMap[createdBy] : undefined);
    }
    if (type === 'CONTRATO') {
      const consultor = data.consultor as { nome?: string } | null;
      const createdBy = data.created_by as string | undefined;
      return consultor?.nome || (createdBy ? profilesMap[createdBy] : undefined);
    }
    return undefined;
  };

  // 4. Montar timeline de eventos
  const events: TimelineEvent[] = [];

  // Adicionar agendamentos
  agendamentos.forEach((a: any) => {
    const hora = a.hora_agendamento?.slice(0, 5) || a.hora_agendamento;
    events.push({
      id: `agendamento-${a.id}`,
      type: 'AGENDAMENTO',
      title: `Agendamento - ${hora || 'Horário não definido'}`,
      description: formatStatusLabel(a.status),
      timestamp: parseAgendamentoTimestamp(a.data_agendamento, a.hora_agendamento),
      actorName: resolveActor('AGENDAMENTO', a),
      data: a,
      metadata: {
        icon: 'calendar',
        color: '#3b82f6',
        actionable: a.status === 'CONFIRMADO' || a.status === 'PENDENTE'
      }
    });
  });

  // Adicionar movimentações de stage do card (histórico do kanban)
  cardHistory.forEach((h) => {
    const stageAnteriorNome = h.stage_anterior_id ? stagesMap[h.stage_anterior_id] : undefined;
    const stageNovoNome = h.stage_novo_id ? stagesMap[h.stage_novo_id] : undefined;

    const title =
      h.acao === 'movido_para_stage' && stageNovoNome
        ? `Movido para "${stageNovoNome}"`
        : CARD_ACTION_LABELS[h.acao] || formatStatusLabel(h.acao);

    const description =
      h.acao === 'movido_para_stage'
        ? stageAnteriorNome
          ? `De "${stageAnteriorNome}" para "${stageNovoNome ?? '—'}"`
          : `Para "${stageNovoNome ?? '—'}"`
        : h.motivo || '';

    events.push({
      id: `card-history-${h.id}`,
      type: 'STATUS_CHANGE',
      title,
      description,
      timestamp: new Date(h.created_at ?? Date.now()),
      actorName: profilesMap[h.usuario_id],
      data: h,
      metadata: {
        icon: 'git-branch',
        color: '#f59e0b',
        actionable: false
      }
    });
  });

  // Adicionar evento de conversão (se aplicável)
  if (cliente.data_conversao) {
    events.push({
      id: `conversao-${cliente.id}`,
      type: 'CONVERSAO',
      title: 'Convertido para Cliente',
      description: 'Cliente ativo',
      timestamp: new Date(cliente.data_conversao),
      data: cliente,
      metadata: {
        icon: 'check-circle',
        color: '#10b981',
        actionable: false
      }
    });
  }

  // Adicionar contratos
  contratosEnriquecidos.forEach((c: any) => {
    // Extrair valor do bem se disponível
    const valor_bem = c.bens?.preco_bem || c.valor_bem || c.valor_parcela;

    events.push({
      id: `contrato-${c.id}`,
      type: 'CONTRATO',
      title: `Contrato ${c.contrato_numero}`,
      description: formatStatusLabel(c.status),
      timestamp: new Date(c.created_at),
      actorName: resolveActor('CONTRATO', c),
      data: {
        ...c,
        valor_bem
      },
      metadata: {
        icon: 'file-text',
        color: '#8b5cf6',
        actionable: c.status === 'RASCUNHO'
      }
    });
  });

  // 5. Ordenar por data (mais recentes primeiro)
  events.sort(
    (a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime()
  );

  // 6. Calcular estatísticas
  const primeiraInteracao = events.length > 0
    ? new Date(events[events.length - 1].timestamp)
    : new Date(cliente.created_at);

  const ultimaInteracao = events.length > 0
    ? new Date(events[0].timestamp)
    : new Date(cliente.created_at);

  return {
    cliente: {
      id: cliente.id,
      nome: cliente.nome,
      cpf: cliente.cpf,
      email: cliente.email,
      telefone: cliente.tel1 || '',
      status: cliente.status,
      created_at: cliente.created_at
    },
    stats: {
      total_agendamentos: agendamentos.length,
      total_contratos: contratos.length,
      primeira_interacao: primeiraInteracao,
      ultima_interacao: ultimaInteracao
    },
    events
  };
}
